import React, { useEffect, useRef, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { authenticatedApiCall } from '../../../utils/apiClient';

/**
 * Progressive card preview: tiny SM JPEG paints first, HQ swaps in when ready.
 * Thumbs are cached on the server (and pre-warmed on upload / list) so cold
 * waits are rare. Fetch starts only when the card is near the viewport so a
 * grid of 16 does not stampede the API (or a cold PDF raster) all at once.
 */
const DashboardThumb = ({ dashboardId, hasDrawing }) => {
  const { instance, accounts } = useMsal();
  const shellRef = useRef(null);
  const [visible, setVisible] = useState(false);
  const [url, setUrl] = useState(null);
  const [hqReady, setHqReady] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const el = shellRef.current;
    if (!el || !hasDrawing) return undefined;
    const io = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { rootMargin: '240px' }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [hasDrawing]);

  useEffect(() => {
    let revokeSm = null;
    let revokeHq = null;
    let cancelled = false;
    setUrl(null);
    setHqReady(false);
    setFailed(false);
    if (!hasDrawing || !dashboardId || !visible) return undefined;

    const fetchBlob = async (size) => {
      const res = await authenticatedApiCall(
        instance, accounts, 'GET',
        `/ga-dashboards/dashboards/${dashboardId}/thumbnail?size=${size}&v=3`,
        null, { responseType: 'blob' }
      );
      return window.URL.createObjectURL(new Blob([res.data], { type: 'image/jpeg' }));
    };

    (async () => {
      try {
        const smUrl = await fetchBlob('sm');
        if (cancelled) {
          window.URL.revokeObjectURL(smUrl);
          return;
        }
        revokeSm = smUrl;
        setUrl(smUrl);

        try {
          const hqUrl = await fetchBlob('hq');
          if (cancelled) {
            window.URL.revokeObjectURL(hqUrl);
            return;
          }
          revokeHq = hqUrl;
          setUrl(hqUrl);
          setHqReady(true);
          if (revokeSm) {
            window.URL.revokeObjectURL(revokeSm);
            revokeSm = null;
          }
        } catch {
          // keep SM if HQ fails
        }
      } catch (e) {
        if (!cancelled) setFailed(true);
      }
    })();

    return () => {
      cancelled = true;
      if (revokeSm) window.URL.revokeObjectURL(revokeSm);
      if (revokeHq) window.URL.revokeObjectURL(revokeHq);
    };
  }, [instance, accounts, dashboardId, hasDrawing, visible]);

  const shell = {
    width: '100%',
    aspectRatio: '4 / 3',
    borderRadius: 8,
    overflow: 'hidden',
    background: 'linear-gradient(145deg, #f1f5f9 0%, #e2e8f0 100%)',
    marginBottom: 12,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: '1px solid #e2e8f0',
  };

  if (!hasDrawing) {
    return (
      <div style={shell}>
        <span style={{ fontSize: 11, color: '#94a3b8', letterSpacing: 0.3 }}>No drawing</span>
      </div>
    );
  }

  if (failed) {
    return (
      <div ref={shellRef} style={shell}>
        <span style={{ fontSize: 22, opacity: 0.45 }}>🗺</span>
      </div>
    );
  }

  if (!url) {
    return <div ref={shellRef} style={{ ...shell, opacity: 0.7 }} />;
  }

  return (
    <div ref={shellRef} style={shell}>
      <img
        src={url}
        alt=""
        draggable={false}
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'contain',
          display: 'block',
          background: '#fff',
          filter: hqReady ? 'none' : 'contrast(0.98)',
          transition: 'filter .25s ease',
        }}
      />
    </div>
  );
};

export default DashboardThumb;
